import { z } from 'zod'
import { DEFAULT_PROJECT_FPS_OPTIONS, isAllowedProjectFps } from './project-fps'

/**
 * Validation schema for project creation/update form
 */
export const projectFormSchema = z.object({
  name: z
    .string()
    .min(1, '请输入项目名称')
    .max(100, '项目名称不能超过 100 个字符')
    .refine((name) => name.trim().length > 0, {
      message: '项目名称不能只包含空格',
    }),

  description: z
    .string()
    .max(500, '项目描述不能超过 500 个字符')
    .optional()
    .or(z.literal('')),

  width: z
    .number()
    .int('宽度必须是整数')
    .min(320, '宽度不能小于 320px')
    .max(7680, '宽度不能超过 7680px（8K）'),

  height: z
    .number()
    .int('高度必须是整数')
    .min(240, '高度不能小于 240px')
    .max(4320, '高度不能超过 4320px（8K）'),

  fps: z
    .number()
    .int('帧率必须是整数')
    .min(1, '帧率不能小于 1')
    .max(240, '帧率不能超过 240')
    .refine((fps) => isAllowedProjectFps(fps), {
      message: '请选择系统支持的帧率',
    }),

  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, '请输入合法的十六进制颜色值，例如 #000000')
    .optional(),
})

/**
 * Type inferred from the schema
 */
export type ProjectFormData = z.infer<typeof projectFormSchema>

/**
 * Project template interface for preset configurations
 */
export interface ProjectTemplate {
  id: string
  platform: string
  name: string
  namePrefix: string
  width: number
  height: number
  fps: number
}

/**
 * Project templates for common platforms
 * 6 preset configurations with collision-free naming
 */
export const PROJECT_TEMPLATES: readonly ProjectTemplate[] = [
  {
    id: 'youtube-1080p',
    platform: '横版视频',
    name: '横版 1080p',
    namePrefix: '横版项目',
    width: 1920,
    height: 1080,
    fps: 30,
  },
  {
    id: 'vertical-9-16',
    platform: '竖版短视频',
    name: '短视频竖版',
    namePrefix: '竖版项目',
    width: 1080,
    height: 1920,
    fps: 30,
  },
  {
    id: 'instagram-square',
    platform: '方形画布',
    name: '方形 1:1',
    namePrefix: '方形项目',
    width: 1080,
    height: 1080,
    fps: 30,
  },
  {
    id: 'instagram-portrait',
    platform: '竖向海报',
    name: '竖向 4:5',
    namePrefix: '竖向项目',
    width: 1080,
    height: 1350,
    fps: 30,
  },
  {
    id: 'twitter-x',
    platform: '社媒横版',
    name: '社媒横版 16:9',
    namePrefix: '社媒项目',
    width: 1200,
    height: 675,
    fps: 30,
  },
  {
    id: 'linkedin',
    platform: '商务宣传',
    name: '商务宣传横版',
    namePrefix: '宣传项目',
    width: 1200,
    height: 627,
    fps: 30,
  },
] as const

/**
 * Common resolution presets
 * Updated for 2025 social media standards
 */
export const RESOLUTION_PRESETS = [
  // Landscape (16:9)
  { label: '1280×720 (HD)', value: '1280x720', width: 1280, height: 720 },
  { label: '1920×1080 (Full HD)', value: '1920x1080', width: 1920, height: 1080 },
  { label: '2560×1440 (2K)', value: '2560x1440', width: 2560, height: 1440 },
  { label: '3840×2160 (4K)', value: '3840x2160', width: 3840, height: 2160 },
  // Vertical (9:16) - TikTok, Reels, Shorts, Stories
  { label: '1080×1920（短视频竖版）', value: '1080x1920', width: 1080, height: 1920 },
  { label: '720×1280（竖版 720p）', value: '720x1280', width: 720, height: 1280 },
  // Square (1:1) - Instagram, Facebook, LinkedIn feeds
  { label: '1080×1080（方形）', value: '1080x1080', width: 1080, height: 1080 },
  // Portrait (4:5) - Instagram feed optimal
  { label: '1080×1350（竖向海报）', value: '1080x1350', width: 1080, height: 1350 },
  // Ultrawide (21:9)
  { label: '2560×1080（超宽屏）', value: '2560x1080', width: 2560, height: 1080 },
] as const

/**
 * Common FPS presets
 */
export const FPS_PRESETS = [...DEFAULT_PROJECT_FPS_OPTIONS]

/**
 * Default form values
 */
export const DEFAULT_PROJECT_VALUES: ProjectFormData = {
  name: '',
  description: '',
  width: 1920,
  height: 1080,
  fps: 30,
}

/**
 * Get resolution aspect ratio
 */
export function getAspectRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
  const divisor = gcd(width, height)

  const ratioWidth = width / divisor
  const ratioHeight = height / divisor

  // Common aspect ratios
  if (ratioWidth === 16 && ratioHeight === 9) return '16:9'
  if (ratioWidth === 9 && ratioHeight === 16) return '9:16'
  if (ratioWidth === 4 && ratioHeight === 3) return '4:3'
  if (ratioWidth === 3 && ratioHeight === 4) return '3:4'
  if (ratioWidth === 21 && ratioHeight === 9) return '21:9'
  if (ratioWidth === 1 && ratioHeight === 1) return '1:1'
  if (ratioWidth === 2 && ratioHeight === 3) return '2:3'
  if (ratioWidth === 3 && ratioHeight === 2) return '3:2'
  if (ratioWidth === 4 && ratioHeight === 5) return '4:5'
  if (ratioWidth === 5 && ratioHeight === 4) return '5:4'

  return `${ratioWidth}:${ratioHeight}`
}
